import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, FileText, Tag, Image as ImageIcon, Compass, Palette,
  Contact as ContactIcon, Inbox, Settings, BookOpen, LogOut, Globe
} from "lucide-react";
import { useAuth } from "../../context/AppContext";
import { api } from "../../lib/api";
import AdminPages from "./AdminPages";
import AdminPromotions from "./AdminPromotions";
import AdminMedia from "./AdminMedia";
import AdminNavigation from "./AdminNavigation";
import AdminBranding from "./AdminBranding";
import AdminContactInfo from "./AdminContactInfo";
import AdminInquiries from "./AdminInquiries";
import AdminSettings from "./AdminSettings";
import AdminGuide from "./AdminGuide";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "pages", label: "Pages & Sections", icon: FileText },
  { id: "promotions", label: "Promotions", icon: Tag },
  { id: "media", label: "Media Library", icon: ImageIcon },
  { id: "navigation", label: "Navigation & Buttons", icon: Compass },
  { id: "branding", label: "Branding & Design", icon: Palette },
  { id: "contact", label: "Contact Information", icon: ContactIcon },
  { id: "inquiries", label: "Customer Inquiries", icon: Inbox },
  { id: "settings", label: "Website Settings", icon: Settings },
  { id: "guide", label: "Owner Guide", icon: BookOpen },
];

function Overview({ goTab }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/admin/promotions").catch(() => ({ data: [] })),
      api.get("/admin/inquiries").catch(() => ({ data: [] })),
      api.get("/admin/media").catch(() => ({ data: [] })),
    ]).then(([p, i, m]) => {
      setStats({
        published: p.data.filter((x) => x.status === "published").length,
        drafts: p.data.filter((x) => x.status === "draft").length,
        newInquiries: i.data.filter((x) => x.status === "New").length,
        totalInquiries: i.data.length,
        media: m.data.length,
      });
    });
  }, []);

  const cards = [
    { label: "Published promotions", value: stats?.published ?? "–", tab: "promotions" },
    { label: "Draft promotions", value: stats?.drafts ?? "–", tab: "promotions" },
    { label: "New inquiries", value: stats?.newInquiries ?? "–", tab: "inquiries" },
    { label: "Total inquiries", value: stats?.totalInquiries ?? "–", tab: "inquiries" },
    { label: "Photos in library", value: stats?.media ?? "–", tab: "media" },
  ];

  return (
    <div data-testid="admin-overview">
      <h2 className="font-heading text-3xl font-semibold text-[#0B1D3A] mb-2">Welcome back, Lisa</h2>
      <p className="text-slate-500 text-sm mb-8">Here's a quick look at your website. New here? Open the <button onClick={() => goTab("guide")} className="text-amber-700 font-semibold underline">Owner Guide</button>.</p>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <button key={c.label} onClick={() => goTab(c.tab)} data-testid={`overview-card-${c.tab}`}
            className="bg-white border border-slate-200 rounded-xl p-5 text-left hover:border-amber-500/60 hover:shadow-md transition-all">
            <p className="text-3xl font-heading font-semibold text-[#0B1D3A]">{c.value}</p>
            <p className="text-xs text-slate-500 mt-1">{c.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-amber-500/30 border-t-amber-600 rounded-full animate-spin" aria-label="Loading" />
      </div>
    );
  }
  if (user === false) {
    navigate("/admin", { replace: true });
    return null;
  }

  const signOut = async () => {
    await logout();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="admin-dashboard">
      <aside className="w-16 lg:w-64 bg-[#061325] text-slate-300 flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        <div className="px-4 py-5 border-b border-amber-500/15">
          <p className="font-heading text-xl text-amber-100 font-semibold hidden lg:block">LD Travel</p>
          <p className="text-[10px] tracking-[0.25em] text-amber-400/70 uppercase hidden lg:block">Dashboard</p>
          <p className="font-heading text-xl text-amber-100 font-semibold lg:hidden text-center">LD</p>
        </div>
        <nav className="flex-1 py-3" aria-label="Dashboard sections">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-nav-tab-${t.id}`}
              aria-current={tab === t.id ? "page" : undefined}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                tab === t.id ? "bg-white/8 text-amber-300 border-r-2 border-amber-400" : "hover:bg-white/5 hover:text-amber-100"
              }`}>
              <t.icon className="w-5 h-5 shrink-0" aria-hidden="true" />
              <span className="hidden lg:inline">{t.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-amber-500/15 space-y-1">
          <Link to="/" data-testid="admin-preview-website-button" className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg hover:bg-white/5 hover:text-amber-100 transition-colors">
            <Globe className="w-5 h-5 shrink-0" aria-hidden="true" /> <span className="hidden lg:inline">View Website</span>
          </Link>
          <button onClick={signOut} data-testid="admin-logout-button" className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg hover:bg-white/5 hover:text-amber-100 transition-colors">
            <LogOut className="w-5 h-5 shrink-0" aria-hidden="true" /> <span className="hidden lg:inline">Sign Out</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 p-5 sm:p-8 max-w-[1200px]">
        {tab === "overview" && <Overview goTab={setTab} />}
        {tab === "pages" && <AdminPages />}
        {tab === "promotions" && <AdminPromotions />}
        {tab === "media" && <AdminMedia />}
        {tab === "navigation" && <AdminNavigation />}
        {tab === "branding" && <AdminBranding />}
        {tab === "contact" && <AdminContactInfo />}
        {tab === "inquiries" && <AdminInquiries />}
        {tab === "settings" && <AdminSettings />}
        {tab === "guide" && <AdminGuide />}
      </main>
    </div>
  );
}
